@use "ui/styles/colors";
@use "ui/styles/sizes";
@use "ui/styles/effects";
@use "ui/styles/fonts";

.trigger {
    position: relative;
    background-color: var(--background-color);
    border: 1px solid var(--input-border-color);
    border-radius: effects.$borderRadiusS;
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 sizes.$s5;
    max-width: 100%;
    gap: sizes.$s3;
    transition: border-color 0.2s ease-in;

    span:first-child {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &[data-disabled] {
        cursor: not-allowed;
        color: var(--disabled-color);
        font-style: italic;

        .icon:hover {
            background-color: transparent;
        }
    }

    &:not([data-disabled]):hover {
        border-color: var(--border-color-hover);
    }

    &[data-placeholder] {
        color: var(--placeholder-text-color);
    }

    &:focus-visible {
        border-color: var(--focus-ring);
        box-shadow: 0 0 0 2px rgba(colors.$blue, 0.2);
    }

    &[data-state="open"] {
        border-color: var(--focus-ring);
        box-shadow: 0 0 0 2px rgba(colors.$blue, 0.2); // TODO: tokenize
    }
}

// Trigger height variants. `large` matches the default Input height; `mediumLarge` matches
// Input's `inputSize="mediumLarge"` (a literal 28px in the design system).
.large {
    min-height: var(--input-height);
}

.mediumLarge {
    min-height: 28px;
}

.value {
    flex: 1;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: fonts.$medium;
}

.icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--secondary-text-color);
    transition: transform 0.2s ease-in;
    @include effects.iconButton;

    svg {
        width: 0.8em;
        height: 0.8em;
    }

    .trigger[data-state="open"] & {
        transform: rotate(180deg);
    }
}

.content {
    background: var(--background-color);
    border-radius: effects.$borderRadiusS;
    overflow: visible;
    box-shadow: 0px 4px 8px 0px effects.$shadowColor;
    border: 1px solid var(--border-color, colors.$gray20);
    z-index: 10000; // Match MultiSelect z-index for modal compatibility
    min-width: var(--radix-select-trigger-width);
    max-height: var(--radix-select-content-available-height);
    user-select: none;
    // Animation properties for enter/exit
    animation-duration: 200ms;
    animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
    will-change: transform, opacity;

    &.constrainWidth {
        width: var(--radix-select-trigger-width);
    }

    &[data-state="open"] {
        animation-name: slideUpAndFade;

        &[data-side="top"] {
            animation-name: slideDownAndFade;
        }

        &[data-side="right"] {
            animation-name: slideLeftAndFade;
        }

        &[data-side="bottom"] {
            animation-name: slideUpAndFade;
        }

        &[data-side="left"] {
            animation-name: slideRightAndFade;
        }
    }

    &[data-state="closed"] {
        animation-name: fadeOut;
    }
}

.viewport[data-radix-select-viewport] {
    padding: sizes.$s2;
    max-height: 350px;
    gap: sizes.$s1;
    display: flex;
    flex-direction: column;
    // Firefox scrollbar styling
    scrollbar-width: thin;
    scrollbar-color: var(--scrollbar-thumb-color) transparent;
    transition: scrollbar-color 0.2s ease;
    --scrollbar-thumb-color: transparent;

    // WebKit scrollbar styling (Chrome, Safari, Edge)
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: var(--scrollbar-thumb-color);
        border-radius: 3px;
    }

    &:hover {
        --scrollbar-thumb-color: colors.$gray10;
    }
}

.group {
    gap: sizes.$s1;
    display: flex;
    flex-direction: column;
}

.item {
    color: var(--text-color);
    border-radius: effects.$borderRadiusXS;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    min-height: 32px;
    padding: 0 sizes.$s4;
    position: relative;
    user-select: none;
    cursor: pointer;
    outline: none;
    transition: background-color 0.2s ease-in;
    gap: sizes.$s4;

    &[data-disabled] {
        color: var(--disabled-color);
        pointer-events: none;
        opacity: 0.5;
    }

    &[data-highlighted] {
        background: var(--secondary-background-color);
        color: var(--text-color);
    }

    &[data-state="checked"] {
        background: var(--selected-primary-background-color);
        color: var(--primary-color);
    }
}

.itemText {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.itemIndicator {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color);
    margin-left: auto;

    svg {
        width: 0.8em;
        height: 0.8em;
    }
}

.label {
    min-height: 32px;
    padding: sizes.$s2 sizes.$s4;
    color: var(--secondary-text-color);
}

.separator {
    height: 1px;
    border-bottom: 1px dotted var(--border-color);
    margin: sizes.$s3 0;
}

// Animation keyframes
@keyframes slideUpAndFade {
    from {
        opacity: 0;
        transform: translateY(2px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideRightAndFade {
    from {
        opacity: 0;
        transform: translateX(-2px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes slideDownAndFade {
    from {
        opacity: 0;
        transform: translateY(-2px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideLeftAndFade {
    from {
        opacity: 0;
        transform: translateX(2px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes fadeOut {
    from {
        opacity: 1;
    }

    to {
        opacity: 0;
    }
}